import { forwardRef, useCallback, useEffect, useMemo, useState, type ImgHTMLAttributes } from 'react';
import { authenticatedMediaUrl, fetchMediaBytes, getApiBase, resolveMediaUrl, useAuthStore } from '@wordpuppi/shared';

/** Component-owned display bytes. Never put blob URLs into content or a persisted query cache. */
export function useMediaPreview(scopeKey = '') {
  const actor = useAuthStore((state) => state.user?.pid ?? '');
  const token = useAuthStore((state) => state.token);
  const base = getApiBase();
  const cache = useMemo(() => ({ active: true, pending: new Map<string, Promise<string>>(), original: new Map<string, string>(), controllers: new Set<AbortController>() }), [actor, token, base, scopeKey]);
  useEffect(() => {
    cache.active = true;
    return () => {
      cache.active = false;
      for (const controller of cache.controllers) controller.abort();
      for (const url of cache.original.keys()) URL.revokeObjectURL(url);
      cache.pending.clear(); cache.original.clear(); cache.controllers.clear();
    };
  }, [cache]);
  const preview = useCallback((raw: string): Promise<string> => {
    if (!authenticatedMediaUrl(raw)) return Promise.resolve(resolveMediaUrl(raw));
    const pending = cache.pending.get(raw);
    if (pending) return pending;
    const controller = new AbortController(); cache.controllers.add(controller);
    const request = fetchMediaBytes(raw, controller.signal).then(({ bytes, contentType }) => {
      if (!cache.active || controller.signal.aborted) throw new Error('Media preview is no longer active');
      const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: contentType }));
      cache.original.set(url, raw);
      return url;
    }).catch(() => { if (cache.pending.get(raw) === request) cache.pending.delete(raw); throw new Error('Media could not be loaded'); }).finally(() => cache.controllers.delete(controller));
    cache.pending.set(raw, request);
    return request;
  }, [cache]);
  const original = useCallback((display: string) => cache.original.get(display) ?? display, [cache]);
  return { preview, original, contextKey: `${actor}:${base}:${scopeKey}` };
}

export function useMediaDisplayUrl(raw = '', scopeKey = '', enabled = true) {
  const { preview } = useMediaPreview(JSON.stringify([scopeKey, enabled ? raw : '']));
  const [loaded, setLoaded] = useState<{ raw: string; preview: typeof preview; url: string }>();
  const protectedUrl = authenticatedMediaUrl(raw);
  useEffect(() => {
    let active = true;
    if (enabled && raw && protectedUrl) void preview(raw).then((url) => { if (active) setLoaded({ raw, preview, url }); }).catch(() => {});
    return () => { active = false; };
  }, [raw, enabled, protectedUrl, preview]);
  if (!enabled || !raw) return undefined;
  return protectedUrl ? loaded?.raw === raw && loaded.preview === preview ? loaded.url : undefined : resolveMediaUrl(raw);
}

export const MediaImage = forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement> & { scopeKey?: string }>(function MediaImage({ src, scopeKey, ...props }, ref) {
  const display = useMediaDisplayUrl(src, scopeKey);
  return <img {...props} ref={ref} src={display} />;
});
